import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileBarChart2, Search, Download, ChevronLeft, ChevronRight,
  Eye, Filter, Plus, X, Upload, Truck
} from 'lucide-react';
import Layout from '../Layout';
import AutocompleteInput from '../AutocompleteInput';
import { fetchVehicles, createVehicleEntry, submitVehicleExit, fetchCharaTypes, uploadFile } from '../../api';

const PAGE_SIZE = 100;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const calcNet = (entryW, exitW) => {
  const a = parseFloat(entryW), b = parseFloat(exitW);
  if (!a || !b || b === 0) return null;
  return (a - b).toFixed(2);
};

const today = () => new Date().toISOString().split('T')[0];
const nowTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/* ── FileZone ────────────────────────────────────────────── */
function FileZone({ value, onChange, id, required }) {
  return (
    <div className="file-upload-zone">
      <input type="file" id={id} accept="image/*" required={required} onChange={e => onChange(e.target.files[0] || null)} />
      {value
        ? <span className="upload-name"><Upload size={12} style={{ marginRight: 4 }} />{value.name}</span>
        : <span className="upload-text"><Upload size={12} style={{ marginRight: 4 }} />Click to upload weight slip</span>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════ */
function ReportsPage({ userRole, userEmail }) {
  const [entries, setEntries]           = useState([]);
  const [charaTypes, setCharaTypes]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [submitting, setSubmitting]     = useState(false);
  const [search, setSearch]             = useState('');
  const [charaFilter, setCharaFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage]   = useState(1);
  const [entryModal, setEntryModal]     = useState(false);
  const [exitModal, setExitModal]       = useState(false);

  /* default last 7 days */
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(today);

  const [entryForm, setEntryForm] = useState({
    date: '', vehicleNo: '', vehicleWeight: '',
    entryTime: '', driverName: '', driverContact: '',
    charaType: '', entryWeightSlip: null, vehicleImage: null,
  });
  const [exitForm, setExitForm] = useState({
    exitDate: '', exitVehicleNo: '', exitTime: '',
    exitVehicleWeight: '', charaType: '', exitWeightSlip: null,
  });

  /* ── autocomplete pools ─────────────────────────────────── */
  const vehicleNoSugs     = useMemo(() => [...new Set(entries.map(e => e.vehicleNo).filter(Boolean))],      [entries]);
  const driverNameSugs    = useMemo(() => [...new Set(entries.map(e => e.driverName).filter(Boolean))],     [entries]);
  const driverContactSugs = useMemo(() => [...new Set(entries.map(e => e.driverContact).filter(Boolean))],  [entries]);

  /* ── map row ────────────────────────────────────────────── */
  const mapRow = (r) => ({
    id:                 r.id,
    date:               r.date,
    vehicleNo:          r.vehicle_no,
    vehicleWeight:      r.vehicle_weight,
    entryTime:          r.entry_time,
    exitDate:           r.exit_date || '',
    exitTime:           r.exit_time || '',
    exitVehicleWeight:  r.exit_vehicle_weight || '',
    driverName:         r.driver_name,
    driverContact:      r.driver_contact,
    charaType:          r.chara_type || '',
    entryWeightSlipUrl: r.entry_weight_slip_url || null,
    exitWeightSlipUrl:  r.exit_weight_slip_url || null,
    vehicleImageUrl:    r.vehicle_image_url || null,
    exited:             r.exited || false,
  });

  /* ── load ───────────────────────────────────────────────── */
  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchVehicles({ dateFrom, dateTo });
      setEntries(data.map(mapRow));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  const loadCharaTypes = useCallback(async () => {
    try {
      const { data } = await fetchCharaTypes();
      setCharaTypes(data.map(d => d.name));
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadEntries(); loadCharaTypes(); }, [loadEntries, loadCharaTypes]);

  /* ── modal auto-fill ────────────────────────────────────── */
  useEffect(() => {
    if (entryModal) setEntryForm(p => ({ ...p, date: today(), entryTime: nowTime() }));
  }, [entryModal]);
  useEffect(() => {
    if (exitModal)  setExitForm(p  => ({ ...p, exitDate: today(), exitTime: nowTime() }));
  }, [exitModal]);
  useEffect(() => {
    document.body.style.overflow = (entryModal || exitModal) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [entryModal, exitModal]);

  /* ── upload slip ────────────────────────────────────────── */
  const uploadSlip = async (file, folder) => {
    if (!file) return null;
    return uploadFile(file, folder);
  };

  /* ── entry submit ───────────────────────────────────────── */
  const handleEntrySubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const slipUrl       = await uploadSlip(entryForm.entryWeightSlip, 'entry');
      const vehicleImgUrl = await uploadSlip(entryForm.vehicleImage, 'vehicle');
      const { data } = await createVehicleEntry({
        date:                entryForm.date,
        vehicleNo:           entryForm.vehicleNo,
        vehicleWeight:       parseFloat(entryForm.vehicleWeight),
        entryTime:           entryForm.entryTime,
        driverName:          entryForm.driverName,
        driverContact:       entryForm.driverContact,
        charaType:           entryForm.charaType || null,
        entryWeightSlipUrl:  slipUrl,
        vehicleImageUrl:     vehicleImgUrl,
      });
      setEntries(p => [mapRow(data), ...p]);
      setEntryModal(false);
      setEntryForm({ date:'', vehicleNo:'', vehicleWeight:'', entryTime:'', driverName:'', driverContact:'', charaType:'', entryWeightSlip:null, vehicleImage:null });
    } catch (err) { alert('Error: ' + err.message); }
    setSubmitting(false);
  };

  /* ── exit submit ────────────────────────────────────────── */
  const handleExitSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    const target = entries.find(en => en.vehicleNo === exitForm.exitVehicleNo && !en.exited);
    if (!target?.id) { alert('Entry not found. Please refresh.'); setSubmitting(false); return; }
    try {
      const slipUrl = await uploadSlip(exitForm.exitWeightSlip, 'exit');
      await submitVehicleExit(target.id, {
        exitDate:            exitForm.exitDate,
        exitTime:            exitForm.exitTime,
        exitVehicleWeight:   parseFloat(exitForm.exitVehicleWeight),
        exitWeightSlipUrl:   slipUrl,
        charaType:           exitForm.charaType || null,
      });
      setEntries(p => p.map(en => en.id === target.id
        ? { ...en, exitDate: exitForm.exitDate, exitTime: exitForm.exitTime,
            exitVehicleWeight: exitForm.exitVehicleWeight,
            exitWeightSlipUrl: slipUrl, charaType: exitForm.charaType || en.charaType,
            exited: true }
        : en));
      setExitModal(false);
      setExitForm({ exitDate:'', exitVehicleNo:'', exitTime:'', exitVehicleWeight:'', charaType:'', exitWeightSlip:null });
    } catch (err) { alert('Error: ' + err.message); }
    setSubmitting(false);
  };

  const openExitModal = (vehicleNo) => {
    const en = entries.find(e => e.vehicleNo === vehicleNo && !e.exited);
    setExitForm(p => ({ ...p, exitVehicleNo: vehicleNo, charaType: en?.charaType || '' }));
    setExitModal(true);
  };

  /* ── unique chara types in results ─────────────────────── */
  const allCharaTypes = useMemo(() =>
    [...new Set(entries.map(e => e.charaType).filter(Boolean))].sort()
  , [entries]);

  /* ── filter ─────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    let list = entries;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        e.vehicleNo?.toLowerCase().includes(q) ||
        e.driverName?.toLowerCase().includes(q) ||
        e.charaType?.toLowerCase().includes(q) ||
        e.driverContact?.includes(q)
      );
    }
    if (charaFilter)              list = list.filter(e => e.charaType === charaFilter);
    if (statusFilter === 'open')  list = list.filter(e => !e.exited);
    if (statusFilter === 'exit')  list = list.filter(e =>  e.exited);
    return list;
  }, [entries, search, charaFilter, statusFilter]);

  /* ── pagination ─────────────────────────────────────────── */
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const pageNums = useMemo(() =>
    Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
      .reduce((acc, p, idx, arr) => {
        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
        acc.push(p); return acc;
      }, [])
  , [totalPages, currentPage]);

  /* ── CSV export ─────────────────────────────────────────── */
  const exportCSV = () => {
    const headers = [
      '#', 'Date', 'Vehicle No', 'Chara Type',
      'In Weight (kg)', 'Out Weight (kg)', 'Net Weight (kg)',
      'In Time', 'Out Time', 'Exit Date',
      'Driver Name', 'Driver Contact', 'Status',
    ];
    const rows = filtered.map((e, i) => [
      i + 1, e.date, e.vehicleNo, e.charaType || '',
      e.vehicleWeight, e.exitVehicleWeight || '',
      calcNet(e.vehicleWeight, e.exitVehicleWeight) || '',
      e.entryTime, e.exitTime || '', e.exitDate || '',
      e.driverName, e.driverContact,
      e.exited ? 'Exited' : 'Open',
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `vehicle-report-${dateFrom}-to-${dateTo}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  /* ── quick preset ───────────────────────────────────────── */
  const applyPreset = (days) => {
    const d = new Date(); d.setDate(d.getDate() - days);
    setDateFrom(d.toISOString().split('T')[0]);
    setDateTo(today());
    setCurrentPage(1);
  };


  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <Layout userRole={userRole} userEmail={userEmail}
      pageTitle="Vehicle Reports" pageSubtitle="Filter, view and export all vehicle data">

      {/* ── Top Actions ─────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn-primary-dash" onClick={() => setEntryModal(true)}>
          <Plus size={15} /> New Vehicle Entry
        </button>
        <button className="btn-ghost" onClick={exportCSV}>
          <Download size={14} /> Export CSV
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {[7, 15, 30, 90].map(d => (
            <button key={d} className="chart-tab" onClick={() => applyPreset(d)}>
              Last {d}D
            </button>
          ))}
        </div>
      </div>

      {/* ── Date + Filter Bar ────────────────────────────────── */}
      <div className="section-card" style={{ marginBottom: 20 }}>
        <div className="section-header">
          <div className="section-title"><Filter size={15} />Filters</div>
          <span style={{ fontSize: 12, color: '#6B7280' }}>
            {filtered.length} records in range
          </span>
        </div>
        <div style={{ padding: '14px 20px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Date range */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>From</div>
            <input type="date" value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setCurrentPage(1); }}
              style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13.5, outline: 'none' }} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>To</div>
            <input type="date" value={dateTo}
              onChange={e => { setDateTo(e.target.value); setCurrentPage(1); }}
              style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13.5, outline: 'none' }} />
          </div>
          {/* Chara type */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>Chara Type</div>
            <select value={charaFilter} onChange={e => { setCharaFilter(e.target.value); setCurrentPage(1); }}
              style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13.5, outline: 'none', minWidth: 150 }}>
              <option value="">All Types</option>
              {allCharaTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {/* Status */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>Status</div>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13.5, outline: 'none', minWidth: 120 }}>
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="exit">Exited</option>
            </select>
          </div>
          <button className="btn-primary-dash" onClick={loadEntries} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </button>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────── */}
      <div className="section-card">
        <div className="section-header">
          <div className="section-title"><FileBarChart2 size={16} />All Entries</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" onClick={exportCSV}>
              <Download size={13} /> Export CSV
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="filter-bar">
          <div className="search-input-wrap">
            <Search size={15} />
            <input className="search-input"
              placeholder="Search by vehicle no, driver name, chara type…"
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>

        <div className="modern-table-wrap">
          <table className="modern-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Vehicle No.</th>
                <th>In Time</th>
                <th>Out Time</th>
                <th>Driver Name</th>
                <th>Driver Contact</th>
                <th>In Weight (kg)</th>
                <th>Out Weight (kg)</th>
                <th>Net Weight (kg)</th>
                <th>Chara Type</th>
                <th>In Slip</th>
                <th>Out Slip</th>
                <th>Vehicle Image</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={15} style={{ textAlign: 'center', padding: '40px 0', color: '#6B7280' }}>
                    <div className="spinner-border spinner-border-sm" style={{ marginRight: 8, color: '#1E5FA8' }} />
                    Loading…
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={15} style={{ textAlign: 'center', padding: '40px 0', color: '#6B7280', fontSize: 14 }}>
                    No records found for the selected filters.
                  </td>
                </tr>
              ) : paginated.map((e, idx) => {
                const net = calcNet(e.vehicleWeight, e.exitVehicleWeight);
                return (
                  <tr key={e.id || idx}>
                    <td style={{ color: '#9CA3AF', fontSize: 12 }}>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                    <td>{fmtDate(e.date)}</td>
                    <td><strong>{e.vehicleNo}</strong></td>
                    <td>{e.entryTime}</td>
                    <td>{e.exitTime || <span style={{ color: '#9CA3AF' }}>—</span>}</td>
                    <td>{e.driverName}</td>
                    <td style={{ color: '#6B7280' }}>{e.driverContact}</td>
                    <td>{e.vehicleWeight}</td>
                    <td>{e.exitVehicleWeight || <span style={{ color: '#9CA3AF' }}>—</span>}</td>
                    <td>
                      {net
                        ? <strong style={{ color: '#1E5FA8' }}>{net}</strong>
                        : <span style={{ color: '#9CA3AF' }}>—</span>}
                    </td>
                    <td>
                      {e.charaType
                        ? <span className="badge-pill primary">{e.charaType}</span>
                        : <span style={{ color: '#9CA3AF' }}>—</span>}
                    </td>
                    <td>
                      {e.entryWeightSlipUrl
                        ? <a href={e.entryWeightSlipUrl} target="_blank" rel="noreferrer" className="btn-view">
                            <Eye size={11} /> View
                          </a>
                        : <span style={{ color: '#9CA3AF' }}>—</span>}
                    </td>
                    <td>
                      {e.exitWeightSlipUrl
                        ? <a href={e.exitWeightSlipUrl} target="_blank" rel="noreferrer" className="btn-view">
                            <Eye size={11} /> View
                          </a>
                        : <span style={{ color: '#9CA3AF' }}>—</span>}
                    </td>
                    <td>
                      {e.vehicleImageUrl
                        ? <a href={e.vehicleImageUrl} target="_blank" rel="noreferrer" className="btn-view">
                            <Eye size={11} /> View
                          </a>
                        : <span style={{ color: '#9CA3AF' }}>—</span>}
                    </td>
                    <td>
                      {e.exited
                        ? <span className="badge-pill success">Exited</span>
                        : <button className="btn-exit" onClick={() => openExitModal(e.vehicleNo)}>Exit</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination-wrap">
          <div className="pagination-info">
            Showing {filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
          </div>
          <div className="pagination-btns">
            <button className="page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
              <ChevronLeft size={14} />
            </button>
            {pageNums.map((p, i) =>
              p === '…'
                ? <span key={`e${i}`} style={{ padding: '0 4px', color: '#9CA3AF', fontSize: 13 }}>…</span>
                : <button key={p} className={`page-btn${currentPage === p ? ' active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
            )}
            <button className="page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ══ Entry Modal ══════════════════════════════════════ */}
      {entryModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEntryModal(false)}>
          <div className="modal-box">
            <div className="modal-box-header">
              <div className="modal-box-title">
                <div style={{ background: '#EBF3FF', borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Truck size={18} color="#1E5FA8" />
                </div>
                New Vehicle Entry
              </div>
              <button className="modal-close" onClick={() => setEntryModal(false)}><X size={16} /></button>
            </div>
            <form onSubmit={handleEntrySubmit}>
              <div className="modal-box-body modern-form">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" value={entryForm.date}
                      onChange={e => setEntryForm(p => ({ ...p, date: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>Entry Time</label>
                    <input type="time" value={entryForm.entryTime}
                      onChange={e => setEntryForm(p => ({ ...p, entryTime: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>Vehicle No.</label>
                    <AutocompleteInput id="vehicleNo" value={entryForm.vehicleNo}
                      onChange={e => setEntryForm(p => ({ ...p, vehicleNo: e.target.value }))}
                      suggestions={vehicleNoSugs} placeholder="e.g. RJ19GA1234" required />
                  </div>
                  <div className="form-group">
                    <label>Entry Weight (kg)</label>
                    <input type="number" step="0.01" placeholder="e.g. 14000"
                      value={entryForm.vehicleWeight}
                      onChange={e => setEntryForm(p => ({ ...p, vehicleWeight: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>Driver Name</label>
                    <AutocompleteInput id="driverName" value={entryForm.driverName}
                      onChange={e => setEntryForm(p => ({ ...p, driverName: e.target.value }))}
                      suggestions={driverNameSugs} placeholder="Full name" required />
                  </div>
                  <div className="form-group">
                    <label>Driver Contact</label>
                    <AutocompleteInput id="driverContact" value={entryForm.driverContact}
                      onChange={e => setEntryForm(p => ({ ...p, driverContact: e.target.value }))}
                      suggestions={driverContactSugs} placeholder="10-digit mobile" required />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label>Chara Type</label>
                    <AutocompleteInput id="charaTypeR" value={entryForm.charaType}
                      onChange={e => setEntryForm(p => ({ ...p, charaType: e.target.value }))}
                      suggestions={charaTypes} placeholder="Select or type chara type" minChars={0} required />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label>Entry Weight Slip (Image)</label>
                    <FileZone value={entryForm.entryWeightSlip}
                      onChange={f => setEntryForm(p => ({ ...p, entryWeightSlip: f }))} id="entrySlipR" required />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label>Vehicle Image</label>
                    <FileZone value={entryForm.vehicleImage}
                      onChange={f => setEntryForm(p => ({ ...p, vehicleImage: f }))} id="vehicleImageR" required />
                  </div>
                </div>
              </div>
              <div className="modal-box-footer">
                <button type="button" className="btn-ghost" onClick={() => setEntryModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary-dash" disabled={submitting}>
                  {submitting ? <><span className="spinner-border spinner-border-sm" /> Saving…</> : <><Plus size={15} /> Submit Entry</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══ Exit Modal ═══════════════════════════════════════ */}
      {exitModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setExitModal(false)}>
          <div className="modal-box">
            <div className="modal-box-header">
              <div className="modal-box-title">
                <div style={{ background: '#FFF4E6', borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Truck size={18} color="#F7941D" />
                </div>
                Vehicle Exit — {exitForm.exitVehicleNo}
              </div>
              <button className="modal-close" onClick={() => setExitModal(false)}><X size={16} /></button>
            </div>
            <form onSubmit={handleExitSubmit}>
              <div className="modal-box-body modern-form">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label>Exit Date</label>
                    <input type="date" value={exitForm.exitDate}
                      onChange={e => setExitForm(p => ({ ...p, exitDate: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>Exit Time</label>
                    <input type="time" value={exitForm.exitTime}
                      onChange={e => setExitForm(p => ({ ...p, exitTime: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>Vehicle No.</label>
                    <input type="text" value={exitForm.exitVehicleNo} readOnly
                      style={{ padding: '10px 14px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 14, background: '#F8FAFC', cursor: 'not-allowed' }} />
                  </div>
                  <div className="form-group">
                    <label>Exit Weight (kg)</label>
                    <input type="number" step="0.01" placeholder="e.g. 8000"
                      value={exitForm.exitVehicleWeight}
                      onChange={e => setExitForm(p => ({ ...p, exitVehicleWeight: e.target.value }))} required />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label>Chara Type</label>
                    <AutocompleteInput id="charaTypeExitR" value={exitForm.charaType}
                      onChange={e => setExitForm(p => ({ ...p, charaType: e.target.value }))}
                      suggestions={charaTypes} placeholder="Select or type chara type" minChars={0} required />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label>Exit Weight Slip (Image)</label>
                    <FileZone value={exitForm.exitWeightSlip}
                      onChange={f => setExitForm(p => ({ ...p, exitWeightSlip: f }))} id="exitSlipR" required />
                  </div>
                </div>
              </div>
              <div className="modal-box-footer">
                <button type="button" className="btn-ghost" onClick={() => setExitModal(false)}>Cancel</button>
                <button type="submit" className="btn-accent-dash" disabled={submitting}>
                  {submitting ? <><span className="spinner-border spinner-border-sm" /> Saving…</> : 'Submit Exit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default ReportsPage;
